import { makeStyles } from 'tss-react/mui';
import { getWhiteLabelSignupBackground } from 'utilities/whitelabel';

import { keyframes } from '@mui/system';
import { LIGHT_GREY } from 'utilities/colors';

const signupBackground = getWhiteLabelSignupBackground();
const keyframeAnimation = keyframes`
  0% {
    transform: scale(2);
  }
  50% {
    transform: scale(3);
  }
  100% {
    transform: scale(2);
  }
`;

const dotsAnimation = keyframes`
  to {
    clip-path: inset(0 -1ch 0 0)
  }
`;

export const useLoginPageStyles = makeStyles()((theme) => ({
  spinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    zIndex: '2',
  },
  loadingState: {
    filter: 'blur(2px)',
  },

  // ==== Redesigned login page ====
  page: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${signupBackground})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0.32) 0%, rgba(0, 0, 0, 0.12) 45%, rgba(0, 0, 0, 0.38) 100%)',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: 'calc(100% - 2rem)',
    maxWidth: 420,
    borderRadius: 24,
    padding: '2.75rem 2rem 2rem',
    backgroundColor: '#ffffff',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '2rem 1.25rem 1.5rem',
    },
  },
  logo: {
    width: 180,
    height: 'auto',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.65rem',
    color: theme.palette.text.primary,
  },
  form: {
    width: '100%',
  },
  field: {
    marginTop: theme.spacing(1.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#fafafa',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  signInButton: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    boxShadow: 'none',
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  forgotPasswordLink: {
    marginTop: theme.spacing(1.5),
  },
  divider: {
    width: '100%',
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    '&::before, &::after': {
      borderColor: theme.palette.divider,
    },
  },
  guestButton: {
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  guestDescription: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
  },
  footerText: {
    color: theme.palette.text.secondary,
  },

  // ==== Legacy classes (still used by LoginPageNew / TokenFetchingPage) ====
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formDiv: {
    width: '100%',
  },
  paperContainer: {
    backgroundImage: `url(${signupBackground})`,
    backgroundSize: 'cover',
    width: '100%',
    height: '100vh',
  },
  loginContainer: {
    width: '100%',
    marginTop: '1rem',
  },
  loginHeader: {
    position: 'relative',
    top: '-2rem',
    width: '80%',
    margin: 'auto',
    backgroundColor: `${theme.palette.secondary.main} !important`,
    paddingTop: '2rem',
    paddingBottom: '2rem',
    color: 'white !important',
  },
  input: {
    marginLeft: 'auto',
    marginTop: '2rem',
    marginRight: 'auto',
    width: '80%',
    justifyContent: 'center',
  },
  signinButton: {
    marginLeft: 'auto',
    marginTop: '2rem',
    marginRight: 'auto',
    marginBottom: '2rem',
    width: '80%',
  },
  signinButtonContainer: {
    textAlign: 'center',
  },
  signInRightButton: {
    marginLeft: '.5rem !important',
  },
  oauthSigninButton: {
    width: '13rem',
    margin: '2rem',
  },
  signUpLink: {
    textDecoration: 'none',
    color: 'black',
    marginRight: '0.5rem',
  },
  resetPasswordLink: {
    textDecoration: 'none',
    color: 'black',
  },
  fullHeight: {
    height: '100%',
  },
  authLoadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url(${signupBackground})`,
    backgroundSize: 'cover',
    width: '100%',
    height: '100vh',
  },
  authContent: {
    background: LIGHT_GREY,
    opacity: '0.7',
    width: '35rem',
    height: '15rem',
    border: '1px solid black',
    borderRadius: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  authText: {
    color: theme.palette.default.black,
    marginTop: '2rem',
    fontSize: '2rem',
    clipPath: 'inset(0 1ch 0 0)',
    animation: `${dotsAnimation} 1s steps(3) infinite`,
  },
  authIcon: {
    color: theme.palette.secondary.main,
    marginTop: '3rem',
    animation: `${keyframeAnimation} 2s infinite`,
  },
}));
